import { z } from "zod";

export const REPORT_SETTINGS_TARGET_BLOCK_REASONS = [
    "ownedByAnotherFirm",
    "cannotEditSettings",
] as const;

const BlockReasonSchema = z.enum(REPORT_SETTINGS_TARGET_BLOCK_REASONS);

const AppliedTemplateSchema = z.object({
    templateId: z.string().uuid().nullable(),
    templateName: z.string(),
});

const TargetFactsSchema = z.object({
    workspaceId: z.string().uuid(),
    appliedTemplate: AppliedTemplateSchema.nullable(),
    overrideCount: z.number().int(),
    blockReason: BlockReasonSchema.nullable(),
});

export const ReportSettingsTemplateTargetsResponseSchema = z.object({
    workspaces: z.array(TargetFactsSchema),
    entities: z.array(TargetFactsSchema.extend({ entityId: z.number().int() })),
});

const TargetIdsSchema = z.object({
    workspaceIds: z.array(z.string().uuid()),
    entityIds: z.array(z.number().int()),
});

const hasDuplicates = (ids: Array<string | number>) =>
    new Set(ids).size !== ids.length;

export const ReportSettingsTemplateApplySchema = z
    .object({
        templateId: z.string().uuid().nullable(),
        apply: TargetIdsSchema,
        remove: TargetIdsSchema,
    })
    .superRefine(({ apply, remove }, context) => {
        const targets = [apply, remove];
        const namedWorkspaceIds = targets.flatMap((ids) => ids.workspaceIds);
        const namedEntityIds = targets.flatMap((ids) => ids.entityIds);
        if (namedWorkspaceIds.length + namedEntityIds.length === 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Name at least one client or entity",
            });
        }
        if (hasDuplicates(namedWorkspaceIds) || hasDuplicates(namedEntityIds)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    "A client or entity can be named only once across apply and remove",
            });
        }
    });

export type ReportSettingsTemplateTargetsResponse = z.infer<
    typeof ReportSettingsTemplateTargetsResponseSchema
>;
export type ReportSettingsTemplateApply = z.infer<
    typeof ReportSettingsTemplateApplySchema
>;
